/**
 * Un bloc de la séance et ses séries (specs §12.3, `ARCHITECTURE_V2.md` §4.3).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DEUX RENDUS, PARCE QU'IL Y A DEUX GESTES
 * ─────────────────────────────────────────────────────────────────────────
 *
 * | Bloc | Rendu | Pourquoi |
 * |-|-|-|
 * | **un exercice** | une grille, une ligne par série | on descend les séries |
 * | **plusieurs** | une grille **par tour** | on alterne, puis on recommence |
 *
 * 🔴 **Le second est le troisième retour d'usage** *(29/08/2026)* : « affiche
 * les supersets d'une meilleure façon pour que l'ordre soit facilement
 * respecté ». La version précédente posait deux cartes voisines reliées par une
 * lettre — la lettre dit *qu'ils vont ensemble*, elle ne dit pas **dans quel
 * ordre**. Un superset n'est pas un groupement, c'est une **séquence** : une
 * série de A, une série de B, repos, on recommence. Rangée par tour, la carte
 * *est* la séquence — il n'y a plus rien à reconstituer de tête.
 *
 * ⚠️ **Ce n'était pas un défaut de rendu mais de MODÈLE** : on regroupait par
 * exercice une structure qui se parcourt par tour. Aucun ajustement de la carte
 * n'aurait suffi.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LE REPOS EST AFFICHÉ, PLUS SEULEMENT DÉCOMPTÉ
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 🔴 Premier retour d'usage du même jour. Il était lu pour lancer le
 * chronomètre et n'apparaissait **nulle part** : on ne pouvait pas savoir
 * combien on allait attendre avant d'avoir validé. Il est désormais dans
 * l'en-tête du bloc — et, pour un enchaînement, **entre les tours**, là où il
 * tombe réellement (§4.3 : « le repos du bloc entre les tours »).
 */

import type { ActualSetDTO, ProgressionDTO } from '../../../../shared/types';
import { useState } from 'react';

import { Button, Card, TextField } from '../../components/nourri';
import { GlyphTrash } from '../../components/nourri/glyphs';
import { useFinePointer } from '../../hooks/useFinePointer';
import { useSwipeToDelete } from '../../hooks/useSwipeToDelete';
import { Sheet } from '../../components/ui/primitives';
import { ExerciseThumb } from './ExerciseThumb';
import { RepsSheet } from './RepsWheel';
import type { SessionBlock, SessionGroup } from './sessionGroups';
import { effortLabel, restLabel, resultBody } from './setLabels';
import { formatRpe, nombre, proposition, RPE_VALUES, type SetValues } from './setValues';

/**
 * Le choix du RPE — huit valeurs, plus le retrait.
 *
 * 🔴 **Une `Sheet`, donc par PORTAIL** (§4.5, et le défaut du troisième tour de
 * l'étape 10) : elle s'ouvre depuis une carte qui vit dans une piste
 * potentiellement transformée. Sans portail, elle s'afficherait hors écran.
 *
 * ⚠️ **Des cibles larges plutôt qu'une roulette native.** Un `<select>` aurait
 * été plus court à écrire et ouvre le sélecteur d'iOS — mais on choisit un RPE
 * *entre deux séries*, mains moites, sans regarder longtemps. Huit boutons de
 * 44 px se visent ; une roulette se fait tourner puis valider, soit deux gestes
 * et une visée fine.
 *
 * ⚠️ **« Aucun » est une valeur du choix, pas un bouton à part.** Le RPE est
 * facultatif — une série d'échauffement n'en a pas — et il faut pouvoir revenir
 * en arrière après avoir touché la case par erreur. Le mettre ailleurs
 * obligerait à comprendre qu'il est ailleurs.
 */
function RpeSheet({
  courant,
  onPick,
  onClose,
}: {
  courant: string;
  onPick: (valeur: string) => void;
  onClose: () => void;
}) {
  /*
   * ⚠️ Une valeur déjà en base qui n'est pas dans l'échelle — saisie avant ce
   * changement, ou venue d'une cible de routine — est **ajoutée à la liste**
   * plutôt qu'ignorée. La faire disparaître du choix laisserait sans moyen de
   * la retrouver après l'avoir quittée, et c'est de l'historique (§4.1).
   */
  const actuel = nombre(courant);
  const valeurs =
    actuel !== null && !RPE_VALUES.includes(actuel)
      ? [...RPE_VALUES, actuel].sort((a, b) => a - b)
      : RPE_VALUES;

  return (
    <Sheet open onClose={onClose} title="RPE">
      <p className="n-hint" style={{ marginTop: 0 }}>
        À combien de répétitions de l’échec ? 10 = aucune.
      </p>

      <div className="n-rpe-grid">
        {valeurs.map((valeur) => {
          const actif = actuel === valeur;
          return (
            <Button
              key={valeur}
              variant={actif ? 'selected' : 'secondary'}
              aria-pressed={actif}
              onClick={() => {
                onPick(String(valeur));
              }}
            >
              {formatRpe(valeur)}
            </Button>
          );
        })}
      </div>

      <Button
        variant="secondary"
        onClick={() => {
          onPick('');
        }}
      >
        Aucun RPE
      </Button>
    </Sheet>
  );
}

export interface RowHandlers {
  valeurs: Readonly<Record<string, SetValues>>;
  onChange: (setClientId: string, valeurs: SetValues) => void;
  onToggle: (set: ActualSetDTO) => void;
  precedenteDe: (set: ActualSetDTO) => ActualSetDTO | undefined;
  /**
   * La charge proposée par la règle de progression (V2 étape 7, §4.4).
   *
   * ⚠️ **Par exercice, distribuée par série.** La règle fait monter la charge
   * *de l'exercice* ; c'est `proposition()` qui décide ensuite qu'elle ne vaut
   * que pour les séries de **travail**.
   */
  progressionDe: (set: ActualSetDTO) => ProgressionDTO | null;
  /** Ouvre la fiche de l'exercice — ses deux vues et son exécution. */
  onOpenExercise: (set: ActualSetDTO) => void;
}

/**
 * Une ligne de saisie — **deux rangs, un seul gabarit**.
 *
 * 🔴 **L'unification corrige un défaut d'affichage réel** *(29/08/2026)* :
 * « Exercice et Précédent se superposent » dans un superset. La cause n'était
 * pas un calage à retoucher, c'était **deux gabarits de grille pour une même
 * grille** — l'en-tête en cinq colonnes, les lignes d'un tour en quatre. Deux
 * gabarits qui doivent s'aligner finissent toujours par ne plus s'aligner.
 *
 * Désormais un seul, en zones nommées, partagé par l'en-tête et les lignes :
 *
 *     "info info info info"
 *     "kg   reps rpe  check"
 *
 * ⚠️ **Le second rang est le même partout** — exercice seul ou tour de
 * superset. On doit pouvoir viser la même case sans regarder, d'un bloc à
 * l'autre. Seul le premier rang change : le **rang de la série** dans un
 * exercice seul, la **vignette et le nom** dans un tour, parce que c'est ce qui
 * change d'une ligne à l'autre.
 *
 * ⚠️ **Et c'est ce qui a ramené le RPE.** Il avait été retiré au tour précédent
 * faute de place ; ce qui manquait de place, c'était la ligne, pas le RPE.
 */
function SetRow({
  set,
  handlers,
  disabled,
  onRemove,
}: {
  set: ActualSetDTO;
  handlers: RowHandlers;
  disabled: boolean;
  onRemove: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  /*
   * 🔴 **Le glissement de suppression, repris du journal** *(07/09/2026,
   * demandé)* — `useSwipeToDelete`, le même hook, les mêmes seuils, le même
   * panneau rouge. Deux gestes de suppression aux réglages différents dans la
   * même application se sentiraient au doigt.
   *
   * ⚠️ **Au doigt seulement** (`useFinePointer`) : à la souris, personne ne
   * devine qu'une ligne se fait glisser. Sur un pointeur fin, c'est le bouton
   * du panneau qui reste atteignable — il est toujours dans le DOM, seulement
   * recouvert.
   *
   * ⚠️ **L'état est LOCAL à la ligne**, contrairement au journal qui le
   * remonte pour n'en ouvrir qu'une à la fois. Ici deux lignes ouvertes ne se
   * gênent pas : elles ne portent aucune information qu'on lirait de travers.
   */
  const fine = useFinePointer();
  const swipe = useSwipeToDelete({
    enabled: !disabled && !fine,
    open: ouvert,
    onOpen: () => {
      setOuvert(true);
    },
    onClose: () => {
      setOuvert(false);
    },
    onDelete: onRemove,
  });
  const duree = set.targetDurationSeconds !== null;
  const precedente = handlers.precedenteDe(set);
  const propose = proposition(set, precedente, duree, handlers.progressionDe(set));
  const saisi = handlers.valeurs[set.clientId] ?? {
    /* Une série déjà faite montre ce qu'elle a donné, pas une proposition. */
    mesure: set.status === 'done' ? String((duree ? set.durationSeconds : set.reps) ?? '') : '',
    charge: set.status === 'done' ? String(set.weightKg ?? '') : '',
    rpe: set.status === 'done' ? String(set.rpe ?? '') : '',
  };

  const [rpeOuvert, setRpeOuvert] = useState(false);
  const [repsOuvert, setRepsOuvert] = useState(false);

  const fait = set.status === 'done';
  const passe = set.status === 'skipped';
  const rappel = precedente === undefined ? '—' : resultBody(precedente);
  /*
   * 🔴 **Le RPE du dernier passage va sur SA propre ligne** *(06/09/2026,
   * demandé : « mets-le sur une ligne en dessous pour qu'on puisse tout voir
   * sans que ce soit tronqué »)*.
   *
   * C'était la contrepartie annoncée du resserrement de la veille : la colonne
   * ne fait plus que ≈ 87 px, et « 62,5 kg × 9 · RPE 8 » y demande ≈ 100. Le
   * RPE était donc systématiquement coupé — l'ordre choisi le 29/08 (charge,
   * reps, RPE) faisait qu'on perdait le moins important, mais on le perdait
   * quand même.
   *
   * ⚠️ **Et ça ne coûte AUCUNE hauteur** : la rangée est gouvernée par la coche
   * à 44 px, et deux lignes de rappel en font ≈ 29. Le seul cas où empiler
   * aurait coûté quelque chose est précisément celui où il y avait de la place.
   */
  const effortPrecedent = precedente === undefined ? null : effortLabel(precedente);

  const classes = ['n-set-row'];
  if (fait) classes.push('is-done');
  if (passe) classes.push('is-skipped');

  const champ = (
    cle: 'charge' | 'mesure',
    zone: string,
    libelle: string,
    mode: 'decimal' | 'numeric',
  ) => (
    <TextField
      shape="compact"
      numeric
      inputMode={mode}
      aria-label={`${libelle} — ${set.exerciseName}, série ${String(set.setIndex)}`}
      className={`n-set-row__cell ${zone}`}
      placeholder={propose[cle] === '' ? '—' : propose[cle]}
      value={saisi[cle]}
      disabled={disabled}
      onChange={(event) => {
        handlers.onChange(set.clientId, { ...saisi, [cle]: event.target.value.replace(',', '.') });
      }}
    />
  );

  const contenu = [...classes, 'n-swipe__content'];
  if (swipe.dragging) contenu.push('is-dragging');
  else if (ouvert) contenu.push('is-open');

  return (
    <div className="n-swipe n-set-row__swipe" {...swipe.handlers}>
      {/*
        ⚠️ **Avant le contenu dans le DOM, et posé sous lui** — la mécanique du
        journal : le bouton existe sans qu'on ait à glisser, il est simplement
        recouvert et se découvre à mesure. C'est aussi ce qui rend le rouge
        continu jusqu'au bord quelle que soit la course.
      */}
      <button
        type="button"
        className="n-swipe__action"
        aria-label={`Supprimer la série ${String(set.setIndex)} — ${set.exerciseName}`}
        disabled={disabled}
        onClick={onRemove}
      >
        <GlyphTrash />
        Supprimer
      </button>

      <div
        className={contenu.join(' ')}
        /* 🔴 Un `transform` en ligne pendant le geste SEULEMENT : au repos la
           position vient d'une classe, sinon un style en ligne gagnerait contre
           la règle `:focus-visible` qui ouvre la ligne quand le bouton prend la
           mise au point — et ce qu'on active cesserait d'être ce qu'on voit. */
        style={
          swipe.offset === null
            ? undefined
            : { transform: `translateX(${String(swipe.offset)}px)` }
        }
      >
      {/*
        🔴 **Le rang seul — l'identité de l'exercice a quitté la ligne**
        *(07/09/2026)*. Dans un enchaînement, elle est désormais en **tête de
        bloc**, une ligne par exercice dans l'ordre ; les lignes d'un tour
        suivent ce même ordre, donc la position **suffit** à dire de quel
        exercice il s'agit.

        ⚠️ **Ce que ça permet, et c'était le but** : une ligne de tour est
        exactement une ligne d'exercice seul, donc **un seul gabarit** — ce que
        le 29/08 avait posé en règle et que la veille avait dû dédoubler.
      */}
      <span className="n-set-row__serie tnum">{String(set.setIndex)}</span>

      {/*
        ⚠️ **Le rappel du dernier passage, centré dans sa colonne**, la mesure
        au-dessus du RPE — cf. le 06/09 : à ≈ 76 px, « 62,5 kg × 9 · RPE 8 » se
        tronquait sur une seule ligne.
      */}
      <span className="n-set-row__prev tnum">
        <span className="n-set-row__prev-main">{rappel}</span>
        {effortPrecedent !== null && (
          <span className="n-set-row__prev-rpe">{effortPrecedent}</span>
        )}
      </span>

      {champ('charge', 'n-set-row__kg', 'Charge', 'decimal')}

      {/*
        🔴 **Les répétitions se tournent, la durée se tape** *(05/09/2026,
        demandé après usage : « comme l'app alarme de l'iPhone »)*.

        Les répétitions sont la valeur la plus souvent saisie de la séance, et
        la seule des trois qui soit un **petit entier borné**. Une roue à
        accrochage se manœuvre d'un pouce sans rien masquer, là où le clavier
        recouvre la moitié de l'écran pour deux touches.

        ⚠️ **La durée garde son champ**, et ce n'est pas une inconséquence : un
        gainage se compte en dizaines de secondes jusqu'à plusieurs minutes, et
        une roue de 300 crans n'est plus une roue. Ce sont deux grandeurs
        différentes, et l'en-tête de colonne le dit déjà — `REPS` contre `SEC`.
        Raisonnement complet en tête de `RepsWheel.tsx`.
      */}
      {duree ? (
        champ('mesure', 'n-set-row__reps', 'Secondes', 'numeric')
      ) : (
        <button
          type="button"
          className={`n-set-row__cell n-set-row__reps n-rpe-pick${
            saisi.mesure === '' ? ' n-rpe-pick--vide' : ''
          }`}
          disabled={disabled}
          aria-label={`Répétitions — ${set.exerciseName}, série ${String(set.setIndex)}`}
          onClick={() => {
            setRepsOuvert(true);
          }}
        >
          {saisi.mesure === '' ? (propose.mesure === '' ? '—' : propose.mesure) : saisi.mesure}
        </button>
      )}

      {repsOuvert && (
        <RepsSheet
          courant={saisi.mesure}
          propose={propose.mesure}
          onClose={() => {
            setRepsOuvert(false);
          }}
          onPick={(valeur) => {
            handlers.onChange(set.clientId, { ...saisi, mesure: valeur });
            setRepsOuvert(false);
          }}
        />
      )}

      {/*
        🔴 **Le RPE se CHOISIT, il ne se tape pas** *(05/09/2026, demandé après
        usage : « comme dans l'app Hevy »)*.

        Ce n'est pas une mesure mais un **jugement** — à combien de répétitions
        de l'échec étais-je ? Entre 8 et 8,5 la différence se porte ; entre 8,2
        et 8,3 il n'y en a aucune, et un champ libre invite pourtant à la
        saisir. Huit valeurs disent tout ce que l'échelle permet de dire.

        ⚠️ **Et cela supprime un clavier**, dans l'écran où il coûte le plus
        cher : mains moites, téléphone posé, entre deux séries. C'est le même
        gain que la coche — un geste au lieu d'une saisie.

        ⚠️ **La géométrie de la cellule ne bouge pas d'un pixel** : le bouton
        reprend les classes du champ compact. La règle de l'étape 6 est nette —
        « les colonnes de saisie ne bougent pas d'un rendu à l'autre : on doit
        pouvoir viser la même case sans regarder ».
      */}
      <button
        type="button"
        className={`n-set-row__cell n-set-row__rpe n-rpe-pick${
          saisi.rpe === '' ? ' n-rpe-pick--vide' : ''
        }`}
        disabled={disabled}
        aria-label={`RPE — ${set.exerciseName}, série ${String(set.setIndex)}`}
        onClick={() => {
          setRpeOuvert(true);
        }}
      >
        {saisi.rpe === '' ? (propose.rpe === '' ? '—' : propose.rpe) : saisi.rpe.replace('.', ',')}
      </button>

      {rpeOuvert && (
        <RpeSheet
          courant={saisi.rpe}
          onClose={() => {
            setRpeOuvert(false);
          }}
          onPick={(valeur) => {
            handlers.onChange(set.clientId, { ...saisi, rpe: valeur });
            setRpeOuvert(false);
          }}
        />
      )}

      {/*
        🔴 **44 × 44** : la cible la plus souvent visée de l'application en
        salle. Le libellé accessible dit l'action et l'exercice — dans un tour,
        « Valider la série 2 » ne suffirait pas à savoir laquelle.
      */}
      <button
        type="button"
        className="n-set-row__check"
        aria-pressed={fait}
        aria-label={`${fait ? 'Annuler' : 'Valider'} — ${set.exerciseName}, série ${String(
          set.setIndex,
        )}`}
        disabled={disabled}
        onClick={() => {
          handlers.onToggle(set);
        }}
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden focusable="false">
          <path
            d="M4 10.5 8 14.5 16 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      </div>
    </div>
  );
}

/**
 * Les libellés de colonnes.
 *
 * ⚠️ **Le MÊME gabarit que les lignes**, zones nommées comprises : c'est ce qui
 * rend la superposition impossible plutôt qu'improbable. Il n'a pas de zone
 * `info`, donc son premier rang se replie à zéro.
 */
/**
 * La note de l'exercice — **un champ éditable, au-dessus des séries**.
 *
 * 🔴 **Le modèle est celui de Hevy** *(29/08/2026, demandé après usage)* : plus
 * de bouton ✎ ni de feuille modale. On écrit là où l'on regarde, entre le nom
 * de l'exercice et ses séries.
 *
 * 🔴 **Et la note SE REPORTE d'une séance à la suivante.** C'est ce que le
 * retour précédent demandait — « elles doivent s'afficher seulement pour la
 * prochaine séance » — et c'est ce que le champ disait déjà de lui-même :
 * « ce qu'il faudra se rappeler la prochaine fois ». Le champ s'ouvre donc sur
 * la note de la séance en cours **si elle existe**, sinon sur celle du dernier
 * passage. Une consigne écrite une fois continue de se présenter tant qu'on ne
 * l'efface pas.
 *
 * ⚠️ **Enregistrée à la PERTE DE FOCUS, jamais à la frappe.** Une écriture par
 * caractère produirait trente opérations en file pour une phrase — et la file
 * draine séquentiellement (§3.3), donc elle retarderait les séries qui, elles,
 * comptent.
 *
 * ⚠️ **Et seulement si le texte a changé.** Sans cette garde, ouvrir puis
 * quitter le champ recopierait la note du passage précédent dans la séance en
 * cours sans qu'on ait rien écrit — ce qui la ferait exister, donc apparaître
 * comme *sienne*, sur une séance où l'on n'a rien à dire.
 *
 * ⚠️ **Vidé, il supprime la note** plutôt que d'en écrire une vide : une note
 * vide et pas de note ne doivent pas coexister en base, sinon `notesByExercise`
 * contiendrait des lignes qui n'affichent rien.
 */
function ExerciseNote({
  valeur,
  onSave,
  disabled,
}: {
  valeur: string;
  onSave: (note: string) => void;
  disabled: boolean;
}) {
  /*
   * ⚠️ **`key` sur l'appelant, état local ici.** Le champ est *non contrôlé* du
   * point de vue du parent pendant la frappe : le contrôler ferait passer
   * chaque caractère par le cache et la file. C'est la même raison que la
   * saisie des séries, qui vit aussi en `useState` (cf. `SessionScreen`).
   */
  const [texte, setTexte] = useState(valeur);

  return (
    <textarea
      className="ui-textarea n-exo-card__note-field"
      rows={2}
      value={texte}
      disabled={disabled}
      placeholder="Ajouter des notes ici…"
      onChange={(event) => {
        setTexte(event.target.value);
      }}
      onBlur={() => {
        if (texte.trim() !== valeur.trim()) onSave(texte.trim());
      }}
    />
  );
}

/**
 * La proposition de charge — **affichée, jamais appliquée** (§4.4).
 *
 * 🔴 **Elle ne s'affiche QUE lorsqu'elle monte**, et c'est une décision.
 * `ProgressionDTO` dit aussi pourquoi on reste (`hold`), mais afficher « on
 * reste à 60 kg » apparaîtrait sur chaque exercice de chaque séance, pour ne
 * rien apprendre : la colonne « précédent » porte déjà cette charge, série par
 * série. Pire, un « tu n'as pas tenu la plage » permanent serait un **jugement**
 * — très exactement ce que l'app s'interdit depuis l'écart kcal de la V1
 * (specs §6.2) et ce qui a fait retirer le rouge des jauges à la refonte.
 *
 * Le motif reste porté par la réponse d'API : diagnostiquable, jamais commenté.
 *
 * ⚠️ **Le chiffre est une proposition, pas une consigne**, et la phrase le dit
 * en montrant d'où l'on part. « 60 → 62,5 kg » se lit d'un coup d'œil et
 * rappelle que la décision reste au lecteur : le champ en dessous est
 * pré-rempli en filigrane, et il s'édite.
 *
 * ⚠️ **Teinte de marque, jamais d'alerte** — la discipline du rappel de pesée
 * du journal : ce n'est pas une erreur, c'est un geste proposé.
 */
function ProgressionNote({ progression }: { progression: ProgressionDTO | null }) {
  if (progression === null || !progression.applied) return null;
  if (progression.weightKg === null || progression.previousWeightKg === null) return null;

  /*
   * 🔴 **Les nombres seuls, sous le nom de l'exercice** *(07/09/2026,
   * demandé)*. Le bandeau portait le mot « PROGRESSION » en capitales et
   * occupait une ligne pleine largeur sous chaque exercice.
   *
   * ⚠️ **Le mot ne disait rien que la flèche ne dise déjà** : « 60 → 62,5 kg »
   * se lit sans étiquette. Et il coûtait une ligne entière par exercice, dans
   * une carte qu'on venait justement de resserrer.
   *
   * ⚠️ **Ce qui reste, c'est le sens** : la proposition n'est toujours pas
   * appliquée (§4.4), elle est **affichée**. Le champ de charge la propose en
   * filigrane ; cette ligne dit d'où elle vient.
   *
   * ⚠️ **Placée dans la colonne du titre**, donc sous le nom et à droite de la
   * vignette : elle se rattache ainsi visuellement à *son* exercice, ce que le
   * §4.4 exige dans un enchaînement — « une proposition qui ne dirait pas
   * lequel des deux exercices elle concerne serait pire qu'absente ».
   */
  return (
    <span className="n-progression tnum">
      {String(progression.previousWeightKg)} → {String(progression.weightKg)} kg
    </span>
  );
}

/**
 * ⚠️ **L'en-tête porte le MÊME modificateur que ses lignes** *(06/09/2026)*.
 * C'est la condition du « un seul gabarit » : deux gabarits qui doivent
 * s'aligner finissent toujours par ne plus s'aligner. Il y en a désormais deux
 * — l'exercice seul sur une rangée, le tour sur deux — mais l'en-tête et ses
 * lignes prennent toujours le même.
 */
/**
 * ⚠️ **Les cinq colonnes sont nommées** *(07/09/2026, demandé)*. Les deux
 * premières ne l'étaient pas : le rang et le rappel se devinaient. « Précédent »
 * en particulier n'avait rien qui le désigne, alors que c'est la colonne dont
 * le sens est le moins évident.
 *
 * 🔴 **Un seul gabarit, partagé par l'en-tête et ses lignes** — la règle du
 * 29/08, redevenue vraie sans exception maintenant que les tours ont le même
 * que les exercices seuls. Deux gabarits qui doivent s'aligner finissent
 * toujours par ne plus s'aligner.
 */
function GridHead({ duree }: { duree: boolean }) {
  return (
    <div className="n-set-row n-set-row--head" aria-hidden>
      <span className="n-set-row__serie">Série</span>
      <span className="n-set-row__prev">Précédent</span>
      <span className="n-set-row__kg">Charge</span>
      <span className="n-set-row__reps">{duree ? 'Sec' : 'Rep'}</span>
      <span className="n-set-row__rpe">RPE</span>
      <span className="n-set-row__check-head" />
    </div>
  );
}

/**
 * Le repos, écrit là où il tombe.
 *
 * ⚠️ **Un filet, pas une ligne de texte parmi d'autres.** Entre deux tours, le
 * repos est une **rupture** : le lire comme une ligne de plus le noierait dans
 * la séquence qu'il sépare.
 */
function RestMark({ seconds }: { seconds: number }) {
  return (
    <p className="n-rest-mark">
      <span>Repos {restLabel(seconds)}</span>
    </p>
  );
}

export function SessionBlockCard({
  bloc,
  restSeconds,
  kindLabel,
  handlers,
  notes,
  previousNotes,
  onAddSet,
  onRemoveSet,
  onNote,
  onOpenExercise,
  disabled,
}: {
  bloc: SessionBlock;
  restSeconds: number;
  /** « Superset », « Circuit » — `null` quand la routine ne le dit plus. */
  kindLabel: string | null;
  handlers: RowHandlers;
  /** La note de la séance **en cours** — elle sert au glyphe du bouton. */
  notes: (exerciseClientId: string | null) => string;
  /**
   * 🔴 **La note du DERNIER PASSAGE, et c'est elle qu'on affiche**
   * *(29/08/2026, demandé après usage : « elles doivent s'afficher seulement
   * pour la prochaine séance »)*.
   *
   * C'est ce que le champ dit déjà de lui-même — son invite est « ce qu'il
   * faudra se rappeler la prochaine fois ». Une note appartient donc à la
   * séance **suivante** : afficher celle qu'on vient d'écrire ne rappelle rien,
   * et occupe la place de celle qui, elle, avait quelque chose à dire.
   */
  previousNotes: (exerciseClientId: string | null) => { note: string; date: string } | null;
  onAddSet: (exercice: SessionGroup) => void;
  /** 🔴 Une **série** désignée, plus le dernier du groupe (07/09/2026). */
  onRemoveSet: (set: ActualSetDTO) => void;
  /** Écrit la note de cet exercice pour la séance **en cours**. */
  onNote: (exercice: SessionGroup, note: string) => void;
  onOpenExercise: (exercice: SessionGroup) => void;
  disabled: boolean;
}) {
  const duree = bloc.exercises.some((exo) =>
    exo.sets.some((set) => set.targetDurationSeconds !== null),
  );

  /*
   * 🔴 **La note de la séance en cours, sinon celle du dernier passage.** C'est
   * ce qui la fait **se reporter** : écrite une fois, elle se représente tant
   * qu'on ne l'efface pas. Le champ ne l'enregistre que si on la modifie —
   * sinon ouvrir puis quitter le champ la recopierait dans une séance où l'on
   * n'a rien eu à dire.
   */
  const noteInitiale = (exercice: SessionGroup): string => {
    const courante = notes(exercice.exerciseClientId);
    if (courante !== '') return courante;
    return previousNotes(exercice.exerciseClientId)?.note ?? '';
  };

  /*
   * ⚠️ **La proposition se lit sur n'importe quelle série du groupe** : elle
   * est calculée par exercice, donc identique pour toutes. Prendre la première
   * évite d'ajouter une seconde entrée à `RowHandlers` pour la même valeur.
   */
  const progressionDe = (exercice: SessionGroup): ProgressionDTO | null => {
    const premiere = exercice.sets[0];
    return premiere === undefined ? null : handlers.progressionDe(premiere);
  };

  /* --- Un exercice seul : la grille des séries -------------------------- */

  if (bloc.rounds === null) {
    const exercice = bloc.exercises[0];
    if (!exercice) return null;

    return (
      <Card padding="none">
        <div className="n-exo-card__head">
          {/*
            🔴 **La vignette, le nom ET le repos forment UNE seule cible**
            *(29/08/2026)* : « quand je clique sur le nom d'un exo ou sa
            vignette, affiche les deux images et sa description ». Prendre le
            bloc entier plutôt que le seul texte rend le geste indulgent — on
            vise un exercice, pas un mot — et évite deux cibles voisines qui se
            disputeraient l'annonce du lecteur d'écran.

            ⚠️ **Le bouton empile, il n'aligne pas** : nom au-dessus, repos
            en-dessous. Les mettre côte à côte les ferait se disputer la largeur
            sur un écran de 375 px, et c'est le nom qui aurait cédé.
          */}
          <button
            type="button"
            className="n-exo-card__open"
            aria-label={`Voir la fiche de ${exercice.exerciseName}`}
            onClick={() => {
              onOpenExercise(exercice);
            }}
          >
            <ExerciseThumb mediaKey={exercice.mediaKey} imageCount={exercice.imageCount} />
            <span className="n-exo-card__title">
              <span className="n-exo-card__name">{exercice.exerciseName}</span>
              {/*
                🔴 **Le repos, enfin visible** — premier retour du 29/08. Il
                était lu pour lancer le chronomètre et ne s'affichait nulle part.
              */}
              <span className="n-exo-card__meta">Repos {restLabel(restSeconds)}</span>
              {/*
                ⚠️ **Dans la colonne du titre ici aussi** *(07/09/2026)* : les
                deux formes de bloc doivent poser la proposition au même
                endroit, sinon on la cherche.
              */}
              <ProgressionNote progression={progressionDe(exercice)} />
            </span>
          </button>
        </div>

        <ExerciseNote
          /*
           * 🔴 **La `key` porte la valeur initiale, et c'est indispensable.**
           * Le dernier passage arrive **après** le premier rendu (c'est une
           * requête) : sans remontage, le champ resterait sur la chaîne vide
           * qu'il avait à l'ouverture, et la note reportée n'apparaîtrait
           * jamais. C'est la même mécanique que le panneau de saisie d'une
           * série — « dérivé au rendu, pas posé dans un effet ».
           */
          key={noteInitiale(exercice)}
          valeur={noteInitiale(exercice)}
          disabled={disabled}
          onSave={(texte) => {
            onNote(exercice, texte);
          }}
        />

        <div>
          <GridHead duree={duree} />
          {exercice.sets.map((set) => (
            <SetRow
              key={set.clientId}
              set={set}
              handlers={handlers}
              disabled={disabled}
              onRemove={() => {
                onRemoveSet(set);
              }}
            />
          ))}
        </div>

        <div className="n-exo-card__actions">
          <Button
            variant="secondary"
            shape="rounded"
            block
            disabled={disabled}
            onClick={() => {
              onAddSet(exercice);
            }}
          >
            + Série
          </Button>
        </div>
      </Card>
    );
  }

  /* --- Un enchaînement : une grille PAR TOUR ---------------------------- */

  const tours = bloc.rounds;

  /*
   * ⚠️ **La première note non vide du bloc.** Elles sont normalement toutes
   * égales — la saisie les écrit toutes —, mais elles peuvent diverger si l'un
   * des exercices a été annoté ailleurs, seul, dans une autre séance. Prendre
   * la première non vide plutôt que rien est le comportement le moins
   * surprenant : on montre ce qu'on a.
   */
  const noteCommune =
    bloc.exercises.map((exercice) => noteInitiale(exercice)).find((texte) => texte !== '') ?? '';


  /*
   * ⚠️ **La vignette est prise sur le GROUPE, pas sur la série.** Une série
   * neuve peut ne pas encore la porter, alors que le groupe la tient dès
   * qu'une seule de ses séries la connaît — cf. `groupSets`, qui la remonte.
   */

  return (
    <Card padding="none" className="n-block-card">
      <div className="n-exo-card__head">
        <div className="n-exo-card__title">
          {/*
            ⚠️ **Le nom vient de la routine quand elle existe encore.** La séance
            ne copie pas `kind` (§4.2 ne copie que ce qui compte), donc on n'a
            rien à en dire quand la routine a changé ou disparu — « Enchaînement »
            reste alors le seul mot vrai. Ce n'est pas grave : c'est la
            **structure en tours** qui porte l'information, pas ce libellé.
          */}
          <span className="n-exo-card__name">{kindLabel ?? 'Enchaînement'}</span>
          <span className="n-exo-card__meta">
            {String(bloc.exercises.length)} exercices · repos {restLabel(restSeconds)} entre les
            tours
          </span>
        </div>
      </div>

      {/*
        🔴 **L'identité des exercices, en TÊTE de bloc et dans l'ordre**
        *(07/09/2026, demandé)*. Elle vivait auparavant sur chaque ligne de
        série — vignette et nom devant les saisies —, ce qui obligeait la ligne
        d'un tour à tenir sur deux rangs quand un exercice seul en prenait un.

        🔴 **C'est ce qui rend le gabarit unique.** Les lignes d'un tour suivent
        cet ordre-ci, donc la **position** suffit à dire de quel exercice il
        s'agit : la première ligne d'un tour est le premier exercice de cette
        liste, la deuxième le deuxième. Une ligne de tour devient alors
        exactement une ligne d'exercice seul.

        ⚠️ **La vignette et le nom restent DANS le bouton qui ouvre la fiche**,
        et pas seulement à côté : c'est la surface qu'on vise naturellement, et
        deux cibles voisines pour la même action se disputeraient l'annonce du
        lecteur d'écran.

        ⚠️ **La proposition de charge reste attachée à SON exercice**, sous son
        nom : « une proposition qui ne dirait pas *lequel* des deux exercices
        elle concerne serait pire qu'absente ». C'est la seule chose qui ne peut
        pas être mise en commun.
      */}
      <div className="n-block-card__exercises">
        {bloc.exercises.map((exercice) => (
          <div key={`exo-${exercice.key}`} className="n-block-card__exercise">
            <button
              type="button"
              className="n-set-row__open"
              onClick={() => {
                onOpenExercise(exercice);
              }}
            >
              <ExerciseThumb
                mediaKey={exercice.mediaKey}
                imageCount={exercice.imageCount}
              />
              <span className="n-exo-card__title">
                <span className="n-set-row__name">{exercice.exerciseName}</span>
                <ProgressionNote progression={progressionDe(exercice)} />
              </span>
            </button>
          </div>
        ))}
      </div>

      {/*
        🔴 **UNE note pour tout l'enchaînement** *(07/09/2026, demandé)*, là où
        il y en avait une par exercice. Le motif d'alors — « deux notes empilées
        sous un superset n'attribueraient rien » — tenait tant que les champs
        étaient **anonymes** ; il tombe maintenant que les exercices sont nommés
        juste au-dessus. Et une consigne de superset parle en général de
        l'enchaînement (« enchaîner sans repos », « alléger le second »), pas
        d'un seul mouvement.

        🔴 **Elle est ÉCRITE SUR CHAQUE exercice du bloc**, et il faut le
        savoir. Le stockage reste `session_exercise_note`, keyé par exercice
        (aucune migration) : la note est donc recopiée sur les deux ou trois
        exercices, et relue comme la **première non vide**.

        ⚠️ **Conséquence assumée** : retrouver cet exercice seul ailleurs y
        affichera cette note. C'est cohérent avec la règle déjà posée le 29/08 —
        « une note parle d'un *mouvement*, pas d'une séance ; la montrer une
        fois de trop ne coûte rien, la cacher fait perdre ce qu'on avait pris la
        peine d'écrire ».

        ⚠️ **Le coût en file est borné** : la note ne s'enregistre qu'à la perte
        de focus **et seulement si le texte a changé** (règle du 29/08), donc
        deux ou trois opérations par saisie réelle, jamais par frappe.
      */}
      <div className="n-exo-card__note-block">
        <ExerciseNote
          key={noteCommune}
          valeur={noteCommune}
          disabled={disabled}
          onSave={(texte) => {
            for (const exercice of bloc.exercises) onNote(exercice, texte);
          }}
        />
      </div>

      {tours.map((tour, rang) => (
        <div key={tour.index} className="n-round">
          <p className="n-round__title">Tour {String(tour.index)}</p>
          <div>
            <GridHead duree={duree} />
            {tour.sets.map((set) => (
              <SetRow
                key={set.clientId}
                set={set}
                handlers={handlers}
                disabled={disabled}
                onRemove={() => {
                  onRemoveSet(set);
                }}
              />
            ))}
          </div>
          {/*
            ⚠️ **Pas de repos après le DERNIER tour** : il n'y a plus rien à
            enchaîner, on passe au bloc suivant. Le poser quand même ferait
            attendre pour rien — et surtout mentirait sur ce qui vient.
          */}
          {rang < tours.length - 1 && <RestMark seconds={restSeconds} />}
        </div>
      ))}

    </Card>
  );
}
